import { useEffect, useState } from "react";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

type NoticeType = "success" | "error" | "warning";

type NoticeChannel = "email" | "whatsapp";

type Notice = {
  id: number;
  type: NoticeType;
  title: string;
  message: string;
  channel: NoticeChannel;
};

const AUTO_CLOSE_MS = 5000;

let nextId = 1;
const listeners = new Set<(notice: Notice) => void>();

/** Show a bottom-right email notification (rendered by <EmailNotifications />). */
export function notifyEmail(
  type: NoticeType,
  title: string,
  message: string,
  channel: NoticeChannel = "email"
) {
  const notice = { id: nextId++, type, title, message, channel };
  listeners.forEach((listener) => listener(notice));
}

/** Same bottom-right pop-up for WhatsApp sends. */
export function notifyWhatsApp(type: NoticeType, title: string, message: string) {
  notifyEmail(type, title, message, "whatsapp");
}

const STYLES: Record<NoticeType, { title: string; icon: JSX.Element }> = {
  success: {
    title: "text-emerald-400",
    icon: <MarkEmailReadOutlinedIcon sx={{ fontSize: 30 }} className="text-emerald-400" />,
  },
  error: {
    title: "text-rose-400",
    icon: <ErrorOutlineIcon sx={{ fontSize: 30 }} className="text-rose-400" />,
  },
  warning: {
    title: "text-amber-400",
    icon: <WarningAmberOutlinedIcon sx={{ fontSize: 30 }} className="text-amber-400" />,
  },
};

/** Host for notifyEmail popups; render once on the page. */
export default function EmailNotifications() {
  const [notices, setNotices] = useState<Notice[]>([]);

  useEffect(() => {
    const listener = (notice: Notice) => {
      setNotices((current) => [...current, notice].slice(-3));
      setTimeout(() => {
        setNotices((current) => current.filter((item) => item.id !== notice.id));
      }, AUTO_CLOSE_MS);
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  const close = (id: number) => setNotices((current) => current.filter((item) => item.id !== id));

  if (!notices.length) return null;

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex w-[calc(100vw-3rem)] max-w-sm flex-col gap-3">
      {notices.map((notice) => (
        <div
          key={notice.id}
          role="status"
          className="flex items-start gap-3 rounded-2xl bg-[#0f1f3d] px-4 py-4 text-white shadow-2xl ring-1 ring-white/10"
        >
          <span className="mt-0.5 shrink-0">
            {notice.channel === "whatsapp" && notice.type === "success" ? (
              <WhatsAppIcon sx={{ fontSize: 30 }} className="text-emerald-400" />
            ) : (
              STYLES[notice.type].icon
            )}
          </span>
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-semibold ${STYLES[notice.type].title}`}>{notice.title}</p>
            <p className="mt-0.5 break-words text-sm text-slate-200">{notice.message}</p>
          </div>
          <button
            type="button"
            onClick={() => close(notice.id)}
            aria-label="Close"
            className="shrink-0 rounded-md p-0.5 text-slate-300 transition hover:bg-white/10 hover:text-white"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </button>
        </div>
      ))}
    </div>
  );
}
